import { Component, EventEmitter, Output, ViewChild } from '@angular/core';
import { isArray } from 'lodash';
import { NefcoDateHelper } from 'src/app/shared/helpers/nefco-date.class';
import { IApiCompanyFilterType, IApiContactFilterType, IApiSageInvoiceFilter, IApiSageInvoiceFilterType } from 'src/app/shared/modules/graphql/types/types';

@Component({
  selector: 'app-bookkeeping-sage-invoice-filters',
  templateUrl: './bookkeeping-sage-invoice-filters.component.html',
  styleUrls: ['./bookkeeping-sage-invoice-filters.component.scss']
})
export class BookkeepingSageInvoiceFiltersComponent {
  private _filters: IApiSageInvoiceFilter[] = [];
  public filterTypes = IApiSageInvoiceFilterType;

  public emailFilterOperator: string = '$contains';
  public emailFilterText: string = '';

  public companyFilterOperator: '$in' | '$notIn' = '$in';
  public companyFilterIds: string[] = [];


  public get filters(): IApiSageInvoiceFilter[] {
    return this._filters;
  }
  public set filters(val: IApiSageInvoiceFilter[]) {
    this._filters = val;
  }

  @ViewChild("startDateInput") public startDateInput: any;
  @ViewChild("endDateInput") public endDateInput: any;
  @ViewChild('startDateRange', { static: false }) public startDateRange: any;
  @ViewChild('endDateRange', { static: false }) public endDateRange: any;

  @ViewChild("dueStartDateInput") public dueStartDateInput: any;
  @ViewChild("dueEndDateInput") public dueEndDateInput: any;
  @ViewChild('dueStartDateRange', { static: false }) public dueStartDateRange: any;
  @ViewChild('dueEndDateRange', { static: false }) public dueEndDateRange: any;

  @Output() public clearFilter = new EventEmitter<IApiSageInvoiceFilter[]>();
  @Output() public applyFilter = new EventEmitter<IApiSageInvoiceFilter[]>();

  public invoiceDateRange: { startDate: Date | null; endDate: Date | null } = { startDate: null, endDate: null };
  public dueDatePickerRange: { startDate: Date | null; endDate: Date | null } = { startDate: null, endDate: null };
  public dueInRange: { start: number | null; end: number | null } = { start: null, end: null };
  public defaultCompanyFilters = [
    {
      type: IApiCompanyFilterType.HasSageKey,
      value: "true"
    }
  ];

  public sageContactFilters = [
    {
      type: IApiContactFilterType.SageContact,
      value: "true"
    },
    {
      type: IApiContactFilterType.ViewUnpaidInvoiceFilter,
      value: "true"
    }
  ];

  public filterByIdValue(filter: IApiSageInvoiceFilterType): any {
    const value = this.filters.find(({ type }) => type === filter)?.value;
    return value ? JSON.parse(value) : null;
  }

  public setFilters(
    value: string | number | any[] | undefined | null,
    type: IApiSageInvoiceFilterType
  ): void {
    const hasValue = (val: any) => val !== undefined && val !== null && val !== "";
    const isString = (val: any): val is string => typeof val === 'string';

    const processedValue = isString(value) ? value.trim() : value;
    const filtersCopy = this.filters.filter(f => f.type !== type);

    this.filters = hasValue(processedValue)
      ? [
        ...filtersCopy,
        {
          type,
          value: Array.isArray(processedValue)
            ? processedValue.length > 0
              ? JSON.stringify(processedValue)
              : null
            : String(processedValue)
        }
      ]
      : filtersCopy;
  }

  public filterValue(filter: IApiSageInvoiceFilterType): any {
    const value = this.filters.find(({ type }) => type === filter)?.value;
    if (value === undefined || value === null) return null;

    if (filter === IApiSageInvoiceFilterType.TotalAmountDue) {
      return Number(value);
    }

    if (filter === IApiSageInvoiceFilterType.DueInDays || value === "true" || value === "false" || (typeof value === 'string' && (value.startsWith('[') || value.startsWith('{')))) {
      try {
        return JSON.parse(value as string);
      } catch {
        return value;
      }
    }

    return value || null;
  }

  public appliedFilters(): void {
    const trimmedFilters = this.filters.map(filter => ({
      ...filter,
      value: typeof filter.value === 'string' ? filter.value.trim() : filter.value
    }));
    this.applyFilter.emit(trimmedFilters);
  }

  public clearAll(): void {
    this.filters = [];
    this.invoiceDateRange = { startDate: null, endDate: null };
    this.dueDatePickerRange = { startDate: null, endDate: null };
    this.dueInRange = { start: null, end: null };
    this.emailFilterOperator = '$contains';
    this.emailFilterText = '';
    this.companyFilterOperator = '$in';
    this.companyFilterIds = [];
    this.clearFilter.emit(this.filters);
  }

  public setVal(event: any, type: IApiSageInvoiceFilterType): void {
    const val = event?.length > 0 ? JSON.stringify(event) : null;
    this.setFilters(val, type);
  }

  public onEmailFilterChange(): void {
    const isEmailInputDisabled = this.emailFilterOperator === '$null' || this.emailFilterOperator === '$notNull';
    const emailKey = "customer.contacts.default.email1";
    if (isEmailInputDisabled) {
      this.emailFilterText = '';
      const mappedOperator = this.emailFilterOperator === '$null' ? '$eq' : '$ne';
      this.setFilters(JSON.stringify({ [mappedOperator]: { [emailKey]: null } }), IApiSageInvoiceFilterType.CustomerEmail);
    } else {
      if (this.emailFilterText && this.emailFilterText.trim().length > 0) {
        this.setFilters(JSON.stringify({ [this.emailFilterOperator]: { [emailKey]: this.emailFilterText.trim().toLowerCase() } }), IApiSageInvoiceFilterType.CustomerEmail);
      } else {
        this.setFilters(null, IApiSageInvoiceFilterType.CustomerEmail);
      }
    }
  }

  public onCompanyFilterChange(filterValue: string | null): void {
    this.setFilters(filterValue, IApiSageInvoiceFilterType.CustomerCompanyName);
  }

  public updateDateFilters(): void {
    this._updateDateRange(
      this.startDateInput?.value,
      this.endDateInput?.value,
      IApiSageInvoiceFilterType.InvoiceDate,
      'invoiceDateRange'
    );
  }

  public updateDueDateFilters(): void {
    this._updateDateRange(
      this.dueStartDateInput?.value,
      this.dueEndDateInput?.value,
      IApiSageInvoiceFilterType.DueDate,
      'dueDatePickerRange'
    );
  }

  private _updateDateRange(
    start: Date | null,
    end: Date | null,
    filterType: IApiSageInvoiceFilterType,
    rangeKey: 'invoiceDateRange' | 'dueDatePickerRange'
  ): void {
    this[rangeKey] = { startDate: start, endDate: end };
    if (start && end) {
      this.setFilters(
        JSON.stringify({
          startDate: NefcoDateHelper.toUtcStartOfDay(start, true),
          endDate: NefcoDateHelper.toUtcStartOfDay(end, false),
        }),
        filterType
      );
    } else {
      this.setFilters(null, filterType);
    }
  }

  public updateAgingFilters(): void {
    if (this.dueInRange.start !== null && this.dueInRange.end !== null) {
      this.setFilters(JSON.stringify(this.dueInRange), IApiSageInvoiceFilterType.DueInDays);
    } else {
      this.setFilters(null, IApiSageInvoiceFilterType.DueInDays);
    }
  }

  public get isFormValid(): boolean {
    const { start, end } = this.dueInRange;
    return !((start !== null && end === null) || (start === null && end !== null));
  }

  public focusDateInput(start = true): void {
    this._focusInput(start ? this.startDateRange : this.endDateRange);
  }

  public focusDueDateInput(start = true): void {
    this._focusInput(start ? this.dueStartDateRange : this.dueEndDateRange);
  }

  private _focusInput(rangeComponent: any): void {
    if (rangeComponent) {
      setTimeout(() => rangeComponent.focus(), 1);
    }
  }
}
